import {
    type ApiContainerAction,
    type ApiPlannedContainerAction,
    EnumContainerActionStatus,
    EnumContainerOrderAction,
    EnumPlannedActionSource,
    isBooked,
} from '@Model';
import { actionKindOf, type ContainerActionKind } from './containerActionKind';
import { type ContainerStockGroup, type ContainerStockRow } from './containerStockGroups';

/** The order the sidebar belongs to, which its stored version shares only by number while edited. */
export interface OwnOrder {
    id: string;
    number: string;
}

/** Whether a planned action belongs to the sidebar's own order, or to its stored version while that is edited. */
export const isOfOwnOrder = (action: ApiPlannedContainerAction, order: OwnOrder) =>
    action.source === EnumPlannedActionSource.Order &&
    (action.source_id === order.id || (!!order.number && action.source_number === order.number));

/** Whether a row follows up on a container at the site and is not booked yet: a pickup, swap or emptying. */
export const isOpenFollowUp = (action: ApiContainerAction) =>
    action.action !== EnumContainerOrderAction.Place && !isBooked(action.booking_status);

/** Whether an action takes its container away, as a pickup or the outgoing half of a swap does; an emptying does not. */
export const takesAway = (action: Pick<ApiContainerAction, 'action'>) =>
    action.action === EnumContainerOrderAction.Collect;

/** Whether a row is confirmed as carried out and gets booked on saving. */
export const isConfirmedRow = (action: ApiContainerAction) =>
    action.booking_status === EnumContainerActionStatus.ToBeBooked;

/** The kind the sidebar names a row by, a swap for either half of an exchange. */
export const kindOfRow = (action: ApiContainerAction): ContainerActionKind =>
    action.exchange_id ? 'swap' : actionKindOf(action.action);

/** Whether a row acts on the standing container with the given number. */
export const rowTakesContainer = (action: ApiContainerAction, containerId: string | undefined) =>
    !!containerId && action.container.foreign_key === containerId;

/** Whether a row acts on a container of a pool group: same type and content, and one standing there if it names one. */
export const rowTakesFromPool = (action: ApiContainerAction, group: ContainerStockGroup) => {
    const standingIds = new Set(group.lines.map((line) => line.container.foreign_key));

    return (
        action.container_type.foreign_key === group.container_type.foreign_key &&
        action.content.foreign_key === group.lines[0].content.foreign_key &&
        (!action.container.foreign_key || standingIds.has(action.container.foreign_key))
    );
};

/** Whether a row takes a standing line of a pool: by its number, or else a line without one of the same content. */
export const rowTakesLine = (action: ApiContainerAction, line: ContainerStockRow) =>
    action.container.foreign_key
        ? action.container.foreign_key === line.container.foreign_key
        : !line.container.foreign_key && action.content.foreign_key === line.content.foreign_key;

/** Whether a row follows up on a coming placement by the number that placement carries. */
export const rowFollowsByNumber = (action: ApiContainerAction, planned: ApiPlannedContainerAction) =>
    !!planned.container?.foreign_key && action.container?.foreign_key === planned.container.foreign_key;

/** Whether a row without a number follows up on a coming placement without one, of the same type and content. */
export const rowFollowsByTypeAndContent = (action: ApiContainerAction, planned: ApiPlannedContainerAction) =>
    !planned.container?.foreign_key &&
    !action.container?.foreign_key &&
    !!planned.container_type?.foreign_key &&
    action.container_type?.foreign_key === planned.container_type.foreign_key &&
    action.content?.foreign_key === planned.content?.foreign_key;

/** Whether another order's pickup, swap or emptying acts on the containers of a stock group. */
export const otherOrderTakesFromStock = (
    taking: ApiPlannedContainerAction,
    group: ContainerStockGroup,
    isPool: boolean,
    order: OwnOrder,
) => {
    const lineIds = new Set(group.lines.map((line) => line.container.foreign_key));

    return (
        taking.action !== EnumContainerOrderAction.Place &&
        taking.source === EnumPlannedActionSource.Order &&
        !isOfOwnOrder(taking, order) &&
        taking.container_type.foreign_key === group.container_type.foreign_key &&
        (isPool
            ? taking.content.foreign_key === group.lines[0].content.foreign_key &&
              (!taking.container.foreign_key || lineIds.has(taking.container.foreign_key))
            : lineIds.has(taking.container.foreign_key))
    );
};

/** Whether another order picks up or swaps out the container a coming placement brings, matched by its number. */
export const otherOrderTakesComing = (
    taking: ApiPlannedContainerAction,
    placement: ApiPlannedContainerAction,
    order: OwnOrder,
) =>
    takesAway(taking) &&
    taking.source === EnumPlannedActionSource.Order &&
    !isOfOwnOrder(taking, order) &&
    !!placement.container.foreign_key &&
    taking.container_type.foreign_key === placement.container_type.foreign_key &&
    taking.container.foreign_key === placement.container.foreign_key;

/** Moves up to `room` rows that follow up on `planned` out of `open`, in their order, so no other card gets them. */
// @ai-generated 2026-10-22T08:00:00+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5
export function claimRows(
    open: ApiContainerAction[],
    planned: ApiPlannedContainerAction,
    room: number,
    takes: (action: ApiContainerAction, planned: ApiPlannedContainerAction) => boolean,
): ApiContainerAction[] {
    const taken: ApiContainerAction[] = [];
    for (let index = 0; index < open.length && taken.length < room; ) {
        if (takes(open[index], planned)) {
            taken.push(...open.splice(index, 1));
        } else {
            index++;
        }
    }

    return taken;
}

type AlikeFields = Pick<ApiContainerAction, 'container_type' | 'container' | 'content' | 'exchange_id'>;

/** The key alike rows and placements share: type, number, content, and whether they belong to an exchange. */
export const alikeKey = (action: AlikeFields) =>
    [
        action.container_type?.foreign_key,
        action.container?.foreign_key,
        action.content?.foreign_key,
        action.exchange_id ? 'exchange' : '',
    ].join('|');

/** The items grouped by their key, in the order the first item of each group appears. */
// @ai-generated 2026-10-19T12:31:15+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5 (1M context)
export function groupAlike<T>(items: T[], keyOf: (item: T) => string): T[][] {
    const groups = new Map<string, T[]>();
    for (const item of items) {
        const key = keyOf(item);
        groups.set(key, [...(groups.get(key) ?? []), item]);
    }

    return [...groups.values()];
}
